import { useEffect, useMemo, useState } from "react";
import type { EngineController } from "../app/controller";
import type { Library } from "../app/library";
import { PracticeService } from "../app/practice";
import { TunerService } from "../app/tuner";
import { SetlistService } from "../app/setlists";
import { SetlistBar } from "./components/band/SetlistBar";
import { Band } from "./workspaces/Band";
import { Notices } from "./components/Notices";
import { NavIcon } from "./components/NavIcon";
import { TopBar } from "./components/TopBar";
import { ServicesProvider } from "./context";
import { LAST_PROJECT_KEY, WORKSPACE_KEY, safeStorageGet, safeStorageSet } from "./prefs";
import { TheoryPanel } from "./workspaces/TheoryPanel";
import { Playground } from "./workspaces/Playground";
import { Studio } from "./workspaces/Studio";
import { LearningLab } from "./workspaces/LearningLab";
import { MyGear } from "./workspaces/MyGear";
import { Projects } from "./workspaces/Projects";

const WORKSPACES = [
  { id: "playground", label: "Playground", short: "Play", hint: "Play instruments, drum pads and samples" },
  { id: "studio", label: "Studio", short: "Studio", hint: "Beat, chord progression and mixer" },
  { id: "learn", label: "Learning Lab", short: "Learn", hint: "Scales, chords, circle of fifths, progression generator, ear training" },
  { id: "band", label: "Band", short: "Band", hint: "Setlists and songs for rehearsals and gigs" },
  { id: "gear", label: "My Gear", short: "Gear", hint: "MIDI devices, audio outputs, capabilities" },
  { id: "projects", label: "Projects", short: "Projects", hint: "Save, open, import and export" },
] as const;

/** Phones get a bottom tab bar and a collapsed theory panel (matches the CSS breakpoint). */
const PHONE_QUERY = "(max-width: 760px)";
function isPhone(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(PHONE_QUERY).matches;
}
type WorkspaceId = (typeof WORKSPACES)[number]["id"];

function isWorkspace(v: string | null): v is WorkspaceId {
  return WORKSPACES.some((w) => w.id === v);
}

const TEXT_INPUT_TYPES = new Set(["text", "number", "search", "email", "url", "password", "tel", ""]);

/** Fields with their own native undo (sliders and checkboxes have none). */
function isTextEntry(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el?.tagName) return false;
  if (el.isContentEditable || el.tagName === "TEXTAREA") return true;
  return el.tagName === "INPUT" && TEXT_INPUT_TYPES.has((el as HTMLInputElement).type);
}

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  return el.isContentEditable || ["INPUT", "SELECT", "TEXTAREA"].includes(el.tagName);
}

export function App({ controller, library }: { controller: EngineController; library: Library }) {
  const services = useMemo(() => ({ controller, library, practice: new PracticeService(controller), tuner: new TunerService(controller), setlists: new SetlistService(controller, library) }), [controller, library]);
  useEffect(() => { void services.practice.load(); void services.setlists.load(); }, [services]);
  const [ws, setWs] = useState<WorkspaceId>(() => {
    const saved = safeStorageGet(WORKSPACE_KEY);
    return isWorkspace(saved) ? saved : "playground";
  });
  const [theoryOpen, setTheoryOpen] = useState(() => !isPhone());

  useEffect(() => safeStorageSet(WORKSPACE_KEY, ws), [ws]);

  // Remember the last saved project for the next visit.
  useEffect(() => controller.doc.subscribe(() => {
    if (controller.doc.get().lastSavedAt) safeStorageSet(LAST_PROJECT_KEY, controller.project.get().id);
  }), [controller]);

  // Global shortcuts: Space = play/stop, Escape = panic (not while typing or on a focused control).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && !e.altKey && !isTextEntry(e.target)) {
        const k = e.key.toLowerCase();
        if (k === "z" && !e.shiftKey) { e.preventDefault(); controller.undo(); return; }
        if ((k === "z" && e.shiftKey) || (k === "y" && e.ctrlKey)) { e.preventDefault(); controller.redo(); return; }
      }
      if (e.repeat || e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return;
      const onButton = (e.target as HTMLElement | null)?.tagName === "BUTTON";
      if (e.code === "Space" && !onButton) { e.preventDefault(); controller.togglePlay(); }
      if (e.key === "Escape") controller.panic();
    };
    const onBlur = () => { controller.noteOffAllUi(); controller.endEditGroup(); };
    // A drag on any slider becomes one undo step.
    const onPointerDown = (e: PointerEvent) => {
      if ((e.target as HTMLInputElement | null)?.type === "range") controller.beginEditGroup();
    };
    const onPointerUp = () => controller.endEditGroup();
    window.addEventListener("keydown", onKey);
    window.addEventListener("blur", onBlur);
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("pointerup", onPointerUp, true);
    window.addEventListener("pointercancel", onPointerUp, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("pointerup", onPointerUp, true);
      window.removeEventListener("pointercancel", onPointerUp, true);
    };
  }, [controller]);

  return (
    <ServicesProvider value={services}>
      <a className="skip-link" href="#workspace">Skip to workspace</a>
      <div className={`app ${theoryOpen ? "with-theory" : ""}`}>
        <div className="top-area">
          <TopBar theoryOpen={theoryOpen} onToggleTheory={() => setTheoryOpen((o) => !o)} />
          <SetlistBar />
        </div>
        <nav className="sidebar" aria-label="Workspaces">
          <p className="app-name"><img src={`${import.meta.env.BASE_URL}icons/logo.svg`} alt="" width={28} height={28} />UmculoLandi</p>
          <ul>
            {WORKSPACES.map((w) => (
              <li key={w.id}>
                <button type="button" className={ws === w.id ? "active" : ""} aria-current={ws === w.id ? "page" : undefined} title={w.hint} aria-label={w.label}
                  onClick={() => { setWs(w.id); if (isPhone()) window.scrollTo({ top: 0 }); }}>
                  <NavIcon name={w.id} />
                  <span className="label-long" aria-hidden="true">{w.label}</span>
                  <span className="label-short" aria-hidden="true">{w.short}</span>
                </button>
              </li>
            ))}
          </ul>
          <button type="button" className="theory-toggle" aria-pressed={theoryOpen} onClick={() => setTheoryOpen((o) => !o)}>
            {theoryOpen ? "Hide" : "Show"} theory
          </button>
          <p className="sidebar-note">Working title.</p>
        </nav>
        <main id="workspace" className="workspace" tabIndex={-1}>
          {ws === "playground" && <Playground />}
          {ws === "studio" && <Studio />}
          {ws === "learn" && <LearningLab />}
          {ws === "band" && <Band />}
          {ws === "gear" && <MyGear />}
          {ws === "projects" && <Projects />}
        </main>
        {theoryOpen && <TheoryPanel />}
        <Notices />
      </div>
    </ServicesProvider>
  );
}
